import { downloadAssetContent, formatFileResponse, FWKIcons, handleRequestError, IAssetVersion, IFile, IRepositoryList, listAssetRepository, Logger, LoginRequired, settings, toast, useFeatureIsEnabled, useIsUser, useUser, useUserEmail } from "@desp-aas/desp-ui-fwk";
import classNames from "classnames";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { partial } from "filesize";
import { useCallback, useEffect, useMemo, useState } from "react";
import { checkFileIsNew, IAssetTabProps } from "../common";
import './Files.css';
import { FileUpload } from "./FileUpload";
import { icons } from "../../../utils/icons";
import { EmailRequiredInfoBox } from "../../../components/EmailRequiredInfoBox/EmailRequiredInfoBox";

const logger = new Logger('asset', 'files')

dayjs.extend(relativeTime)
const size = partial({ standard: "jedec" });

let fetchFilesLoop: NodeJS.Timeout | undefined = undefined;

export default function Files(props: IAssetTabProps & { onUpload?: () => any, latestVersionDate: string|undefined }) {
    const [branch, setBranch] = useState<string>("main");
    const [loadingFiles, setLoadingFiles] = useState<boolean>(false);
    const [files, setFiles] = useState<IFile[] | undefined>([]);
    const user = useUser();
    const currentEmail = useUserEmail();
    const [paths, setPaths] = useState<string[]>([]);
    const isEmailEnabled = useFeatureIsEnabled("email_enabled");
    // const [paths, setPaths] = useState<string[]>(["folder1", "folder2"]);


    
    const collaborationDisabled = useMemo(() => {
        return !user || ( isEmailEnabled ? !currentEmail : false );
    }, [user, currentEmail, isEmailEnabled])
    const downloadDisabled = useMemo(() => {
        return !user || ( isEmailEnabled ? !currentEmail : false );
    }, [user, currentEmail, isEmailEnabled])

    const fileUploadDisabled = useMemo(() => {
        return !props.editMode || ( isEmailEnabled ? !currentEmail : false );
    }, [props.editMode, currentEmail, isEmailEnabled])

    const fetchFiles = useCallback((withoutLoading?: boolean) => {
        if (
            props.asset && props.asset.repository
            && ( 
                props.editMode
                || props.selectedVersion
            )
        ) {
            if(!withoutLoading) setLoadingFiles(true)
            listAssetRepository(
                props.asset.repository.id, 
                "/" + paths.join("/"), props.editMode 
                ? undefined 
                : props.selectedVersion?.marker
            )
                .then((repo: IRepositoryList) => {
                    setFiles(formatFileResponse(repo.files, repo.folders));
                })
                .catch((e) => {
                    setFiles([]);
                    logger.error("An error has occured while fetching files", e)
                    handleRequestError(e, { defaultMessage: "An error has occured during file retrieval. Please try again later" })
                })
                .finally(() => {
                    if(!withoutLoading) setLoadingFiles(false);
                })
        }
        else {
            setFiles(undefined)
        }
    }, [props.editMode, props.selectedVersion, paths])

    useEffect(() => {
        fetchFiles()
    }, [fetchFiles, paths])

    const downloadFiles = useCallback(() => {
        if (!(files && files.length > 0)) return;

    }, [files])

    const gitLink = useMemo(() => {
        return props.asset?.repository ? `/api${settings.api.storage}/repository_link/${props.asset?.repository?.id}` : undefined
    }, [ props.asset ])


    const handleTreeClick = useCallback((file: IFile) => {
        if (file.file_type === 'tree') {
            setPaths((paths)=>[...paths, file.filePath.split("/").pop() || ""])
        }
    }, [])

    const handlePathClick = useCallback((idx: number) => {
        if(idx < paths.length - 1)
            setPaths(paths.slice(0, idx + 1))
    }, [paths])

    const handlePreviousPath = useCallback(() => {
        setPaths((paths)=>paths.slice(0, -1))
    }, [])


    const isAssetOwner = useIsUser(props.asset?.despUserId);

    useEffect(() => {
        if(props.editMode){
            fetchFilesLoop = setInterval(() => {
                fetchFiles(true);
            }, 10000);
        }
        else{
            clearInterval(fetchFilesLoop);
        }
        return () => {
            clearInterval(fetchFilesLoop);
        }
    }, [props.editMode])


    return (
        <div id="asset-files">
            <div id="asset-files-operations">
                <div id="asset-files-collaborate-container">
                        <a
                            id="asset-files-git-link"
                            className={classNames("button external-link inverted", {
                                "disabled": collaborationDisabled
                            })}
                            // onClick={handleCollaborate}
                            
                            href={ collaborationDisabled ? undefined : gitLink}
                            target="_blank"
                        >
                            {icons.externalLink} Collaborate
                        </a>
                        {
                            !user
                            ?
                                <LoginRequired
                                    small
                                    message="Sign in to collaborate"
                                />
                            :
                                !currentEmail
                                ? <EmailRequiredInfoBox 
                                    message={isAssetOwner ? "Email required to upload and view files." : "Email required to collaborate."}
                                />
                                : null
                        }
                </div>
                <a
                    id="asset-files-download"
                    className={classNames({ 
                        "button blue inverted": true, 
                        "disabled": !(files && files.length > 0) || (!props.editMode && !props.selectedVersion) || downloadDisabled
                    })}
                    onClick={downloadDisabled ? undefined : downloadFiles}
                    href={downloadAssetContent(props.asset.id, props.editMode ? undefined : props.selectedVersion?.marker)}
                    download
                    target="_blank"
                >
                    {FWKIcons.download} Download files
                </a>
                {
                    props.editMode &&
                    <FileUpload
                        id="asset-files-upload"
                        asset={props.asset}
                        editMode={props.editMode}
                        onChange={() => { fetchFiles(); props.onUpload?.() }}
                        branch={branch}
                        paths={paths}
                        disabled={fileUploadDisabled}
                    />
                }
            </div>
            <div id="asset-files-path">
                <div className="file-asset-file-path-separator">
                    /
                </div>
                <div 
                    className={classNames({
                        "file-asset-file-path": true,
                        "clickable": paths.length > 0
                    })}
                    onClick={ paths.length > 0 ? ()=>handlePathClick(-1) : undefined}
                >
                    { props.asset.name }
                </div>
                {
                    paths.map((path: string, idx: number) => (
                        <>
                            {
                                // idx <= paths.length - 1 &&
                                <div className="file-asset-file-path-separator" key={idx}>
                                    /
                                </div>
                            }
                            <div 
                                className={classNames({
                                    "file-asset-file-path": true,
                                    "clickable": idx < paths.length - 1
                                })} key={idx}
                                onClick={()=>handlePathClick(idx)}
                            >
                                {path}
                            </div>
                        </>
                    ))
                }
            </div>
            <div id="asset-files-container">
                <div id="asset-files-headers">
                    <div className="asset-files-header name">Name</div>
                    <div className="asset-files-header commit-message">Message</div>
                    <div className="asset-files-header size">Size</div>
                    <div className="asset-files-header time">Last modified</div>
                </div>
                <div id="asset-files-list" className="simple-scrollbar">
                    {
                        loadingFiles
                            ?
                            <>
                                <LoadingFile />
                                <LoadingFile />
                                <LoadingFile />
                                <LoadingFile />
                            </>
                            :
                                files
                                    ?
                                    <>
                                        {
                                            paths.length > 0 &&
                                            <div className="file-item folder back" onClick={handlePreviousPath}>
                                                <div className="file-item-value name">{icons.files.folderUp} ..</div>
                                                <div className="file-item-value commit-message" />
                                                <div className="file-item-value size" />
                                                <div className="file-item-value time" />
                                            </div>
                                        }
                                        {
                                            files.length > 0
                                                ?
                                                files.map((file: IFile, idx: number) => (
                                                    <FileItem key={file.filePath} file={file} handleTreeClick={handleTreeClick} latestVersionDate={props.latestVersionDate} />
                                                ))
                                                :
                                                <div className="no-data centered padding">
                                                    No files present.
                                                </div>
                                        }
                                    </>
                                        :
                                        <div className="no-data centered padding">
                                            Failed to retrieve files.
                                        </div>
                    }
                </div>
            </div>
        </div>
    )
}


type IFileItemProps = {
    file: IFile
    handleTreeClick: (file: IFile) => void
    latestVersionDate: string|undefined
}

const FileItem = (props: IFileItemProps) => {

    const icon = useMemo(() => {
        return props.file.file_type === 'tree' ? icons.files.folder : icons.files.file
    }, [ props.file.file_type ])

    const isNew = useMemo(() => {
        if(!props.latestVersionDate) return false;
        const isNew = checkFileIsNew(props.file, props.latestVersionDate)
        console.log(`Check that file ${props.file.fileName} is new ${props.file.commitDate} > ${props.latestVersionDate} => ${isNew}`)
        return isNew
    }, [ props.file, props.latestVersionDate ])

    return (
        <div 
            className={classNames("file-item", {
                "folder": props.file.file_type === 'tree',
                "new": isNew
            })}key={props.file.filePath}
            onClick={()=>props.handleTreeClick(props.file)}
        >
            <div className="file-item-value name">
                { icon }
                { props.file.fileName}
            </div>
            <div className="file-item-value commit-message">{ props.file.commitMessage}</div>
            <div className="file-item-value size">{size(props.file.size)}</div>
            <div className="file-item-value time">{dayjs(props.file.commitDate).fromNow()}</div>
        </div>
    )
}


export const LoadingFile = () => (
    <div className="loading-file-item loading">
        <div className="loading-file-item-value name " />
        <div className="loading-file-item-value commit-message" />
        <div className="loading-file-item-value size" />
        <div className="loading-file-item-value time" />
    </div>
)